import { signupContext } from "context/SignupContext";
import { signInContext } from "context/SigninContext";
import React, { useContext, useEffect, useState } from "react";
import { ManageUser, Organization, getConnectionPool } from "@induz/sdk";
import "./razorpay.scss";
const RazorpayPayment = ({
  nextStep,
  setSuccessState,
  setStep,
  setChange,
  setIndividualSuccessState,
  onDismissFunction
}) => {
  // State to track if payment is being processed
  const [isProcessing, setIsProcessing] = useState(true);

  // const totalAmount = (100 + 25) * 100; // Example total amount in paise
  const { paymentKey, amount, orderId, currency, isTypeSelect: signupIsTypeSelect, user: signupUser, orgnization: signupOrg, paymentObj, selectedPlanData } =
    useContext(signupContext);
  
  // Also check signInContext as fallback
  const { user: signInUser, orgnization: signInOrg, isTypeSelect: signInIsTypeSelect } = useContext(signInContext);

  // Use user/org from signupContext, fallback to signInContext
  const user = signupUser || signInUser;
  const orgnization = signupOrg || signInOrg;
  const isTypeSelect = signupIsTypeSelect || signInIsTypeSelect || "individual";
  
  // Extract payment details from Payment object (from startPayment method)
  // Payment object structure: { orderId, razorpayPaymentId, amount }
  const razorpayKey = paymentObj?.razorpayPaymentId || paymentKey;
  const paymentOrderId = paymentObj?.orderId || orderId;
  const paymentAmount = paymentObj?.amount || amount;

  useEffect(() => {
    // Check if using test mode and log test payment info
    const isTestMode = razorpayKey && razorpayKey.startsWith("rzp_test_");
    if (isTestMode) {
      console.log("🧪 TEST MODE ENABLED - Use these test cards:");
      console.log("✅ Success: 4111 1111 1111 1111 (any CVV, future expiry)");
      console.log("❌ Failure: 5104 0600 0000 0008 (any CVV, future expiry)");
      console.log("💳 UPI Success: success@razorpay");
      console.log("💳 UPI Failure: failure@razorpay");
      console.log("🔑 Test Key:", razorpayKey);
    }

    // Validate required payment data
    if (!razorpayKey || !paymentOrderId) {
      console.error("Missing payment data. Ensure startPayment() was called and Payment object is set in context.");
      setIsProcessing(false);
      return;
    }

    // Do not open Razorpay when amount is 0 (e.g. coupon covered full price). Treat as success.
    const amountNum = paymentAmount != null ? Number(paymentAmount) : NaN;
    if (amountNum === 0 || (typeof amountNum === "number" && !Number.isNaN(amountNum) && amountNum <= 0)) {
      console.log("✅ Final amount is 0. Skipping Razorpay; marking as successful.");
      setIsProcessing(false);
      if (setSuccessState) setSuccessState(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      const options = {
        key: razorpayKey, // From Payment.razorpayPaymentId (e.g., rzp_test_123456)
        order_id: paymentOrderId, // From Payment.orderId
        amount: paymentAmount, // From Payment.amount (in paise)
        currency: currency || "INR",
        name: "Induz",
        description: "Payment for Professional Plan",
        handler: async (response) => {
          // Payment successful - verify payment using finishPayment
          setIsProcessing(false);

          if (response && paymentObj) {
            console.log("Payment response:", response);
            console.log("Payment object:", paymentObj);
            
            try {
              console.log("🔍 Starting payment verification...");
              
              // Strategy: Use existing user/org from context first (most reliable during signup)
              // Only fetch fresh ones if context objects are not available (for upgrades)
              let currentUser = null;
              let currentOrg = null;
              let currentIsTypeSelect = isTypeSelect;

              // First, try to use existing user/org from context
              if (orgnization && typeof orgnization.finishPayment === "function") {
                currentOrg = orgnization;
                currentIsTypeSelect = "org";
                console.log("✅ Using Organization from signupContext for payment verification");
              } else if (user && typeof user.finishPayment === "function") {
                currentUser = user;
                currentIsTypeSelect = "individual";
                console.log("✅ Using User from signupContext for payment verification");
              } else if (signInOrg && typeof signInOrg.finishPayment === "function") {
                currentOrg = signInOrg;
                currentIsTypeSelect = "org";
                console.log("✅ Using Organization from signInContext for payment verification");
              } else if (signInUser && typeof signInUser.finishPayment === "function") {
                currentUser = signInUser;
                currentIsTypeSelect = "individual";
                console.log("✅ Using User from signInContext for payment verification");
              } else {
                // Context objects not available, try to fetch fresh ones (for upgrades)
                console.log("⚠️ User/org not in context, fetching from backend...");
                
                let userEmail = null;
                
                // Try to get email from connection pool
                try {
                  const pool = await getConnectionPool();
                  userEmail = pool.getEmail();
                  if (userEmail) {
                    console.log("✅ Got email from connection pool:", userEmail);
                  }
                } catch (poolError) {
                  console.warn("⚠️ Could not get email from connection pool:", poolError);
                }

                // Try to get email from existing objects
                if (!userEmail) {
                  if (user && typeof user.getEmail === "function") {
                    userEmail = user.getEmail();
                  } else if (orgnization && typeof orgnization.getEmail === "function") {
                    userEmail = orgnization.getEmail();
                  } else if (signInUser && typeof signInUser.getEmail === "function") {
                    userEmail = signInUser.getEmail();
                  } else if (signInOrg && typeof signInOrg.getEmail === "function") {
                    userEmail = signInOrg.getEmail();
                  }
                }

                // Try sessionStorage as last resort
                if (!userEmail) {
                  userEmail = sessionStorage.getItem("mail");
                }

                // Fetch user/org from backend
                let fetchedUser = null;
                if (userEmail) {
                  try {
                    fetchedUser = await ManageUser.getUserDetails(userEmail);
                    console.log("✅ Fetched user/org with email:", userEmail);
                  } catch (emailError) {
                    console.warn("⚠️ Failed to fetch with email, trying without email:", emailError);
                    try {
                      fetchedUser = await ManageUser.getUserDetails();
                      console.log("✅ Fetched user/org without email (using pool)");
                    } catch (poolError) {
                      throw new Error(`Failed to fetch user details: ${emailError.message || emailError}. Please ensure you are logged in.`);
                    }
                  }
                } else {
                  try {
                    fetchedUser = await ManageUser.getUserDetails();
                    console.log("✅ Fetched user/org without email (using pool)");
                  } catch (poolError) {
                    throw new Error("User email not found and connection pool email unavailable. Please log in again.");
                  }
                }

                if (!fetchedUser) {
                  throw new Error("Failed to fetch user/org from backend. Please try again.");
                }

                if (fetchedUser instanceof Organization) {
                  currentOrg = fetchedUser;
                  currentIsTypeSelect = "org";
                  console.log("✅ Fetched Organization from backend for payment verification");
                } else {
                  currentUser = fetchedUser;
                  currentIsTypeSelect = "individual";
                  console.log("✅ Fetched User from backend for payment verification");
                }
              }

              // Verify payment using the user/org instance
              if (currentIsTypeSelect !== "individual" && currentOrg && typeof currentOrg.finishPayment === "function") {
                // Use finishPayment method from Organization class
                await currentOrg.finishPayment(paymentObj);
                console.log("✅ Payment verified successfully for Organization. Backend will update plan.");
                setSuccessState(true);
              } else if (currentIsTypeSelect === "individual" && currentUser && typeof currentUser.finishPayment === "function") {
                // Use finishPayment method from User class
                await currentUser.finishPayment(paymentObj);
                console.log("✅ Payment verified successfully for User. Backend will update plan.");
                
                // Set success state for navigation
                setSuccessState(true);
                
                if (setIndividualSuccessState)
                  setIndividualSuccessState("payment_success");
                if (setStep) setStep("");
              } else {
                throw new Error("User or Organization object not available or missing finishPayment method.");
              }
            } catch (error) {
              console.error("❌ Payment verification failed:", error);
              // Show user-friendly error message
              const errorMessage = error.message || "Payment verification failed. Please contact support if the payment was successful.";
              alert(`Payment Verification Error: ${errorMessage}\n\nIf your payment was successful, please contact support with your payment ID: ${paymentObj?.orderId || "N/A"}`);
              
              // Handle payment verification error
              if (setIndividualSuccessState)
                setIndividualSuccessState("payment_failed");
            }
          }
        },
        prefill: {
          name: "John Doe",
          email: "john@example.com",
          contact: "9999999999"
        },
        theme: {
          color: "#3399cc"
        },
        modal: {
          ondismiss: function () {
            // Set processing to false as payment modal is dismissed
            setIsProcessing(false);

            // When parent provides onDismissFunction, it controls where to go (e.g. back to payment step).
            // Otherwise we fall back to legacy nextStep(5) / setChange for signup flow compatibility.
            if (onDismissFunction) {
              onDismissFunction();
              return;
            }
            if (isTypeSelect !== "individual") {
              setChange("payment");
              nextStep && nextStep(5);
            } else {
              nextStep && nextStep(5);
            }
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    };
    return () => {
      script.onload = null; // Cleanup
      // Ensure we set processing to false when component unmounts
      setIsProcessing(false);
    };
  }, [razorpayKey, paymentOrderId, paymentAmount, currency, isTypeSelect, orgnization, user, paymentObj]);

  // Only show "Processing Payment..." when actually processing
  if (!isProcessing) {
    return null; // Return nothing when payment is complete or dismissed
  }

  return (
    <div className="paymentPage">
      <h2>Processing Payment...</h2>
    </div>
  );
};

export default RazorpayPayment;
